import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';
import { ArrowRight, CheckCircle2, Send, FileText, ClipboardCheck, Phone, Briefcase, Users, Calendar, Rocket } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DIENST_OPTIES = [
  'Website laten maken',
  'SEO',
  'Google Ads',
  'Social Media Advertising',
  'Leadgeneratie',
  'Content Marketing',
  'Strategie & Groei',
  'Combinatie / overig',
];

const BUDGET_OPTIES = [
  '< €500 / maand',
  '€500 – €1.000 / maand',
  '€1.000 – €2.500 / maand',
  '€2.500 – €5.000 / maand',
  '> €5.000 / maand',
  'Eenmalig project',
  'Nog onbekend',
];

const POST_STEPS = [
  { n: 1, title: 'Aanvraag ontvangen', desc: 'Wij ontvangen uw aanvraag en sturen direct een bevestiging.', icon: FileText },
  { n: 2, title: 'Globale analyse', desc: 'Onze sales bekijkt uw website en huidige situatie globaal.', icon: ClipboardCheck },
  { n: 3, title: 'Adviesgesprek', desc: 'Online of telefonisch overleg om uw doelen te bespreken.', icon: Phone },
  { n: 4, title: 'Offerte op maat', desc: 'U ontvangt een passend voorstel met heldere prijzen.', icon: Briefcase },
  { n: 5, title: 'Onboarding', desc: 'Bij akkoord wordt uw klantaccount aangemaakt en onboarding gestart.', icon: Users },
  { n: 6, title: 'Kick-off met strategie', desc: 'Diepgaande analyse en strategische uitwerking.', icon: Calendar },
  { n: 7, title: 'Start uitvoering', desc: 'Wij voeren uit. U volgt alles live in uw klantdashboard.', icon: Rocket },
];

export default function OffertePage() {
  const [params] = useSearchParams();
  const preDienst = params.get('dienst') || '';
  const [form, setForm] = useState({
    naam: '',
    bedrijfsnaam: '',
    email: '',
    telefoon: '',
    website: '',
    dienst: preDienst,
    budget: '',
    omschrijving: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.naam || !form.email || !form.dienst) {
      toast.error('Vul uw naam, e-mail en gewenste dienst in');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/form-submit`, {
        form_type: 'offerte',
        ...form,
      });
      setSent(true);
      toast.success('Offerteaanvraag ontvangen — wij reageren binnen 24 uur');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Verzending mislukt. Probeer het opnieuw of mail direct.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Offerte Aanvragen — Vrijblijvend & Binnen 24 Uur Reactie"
        description="Vraag vrijblijvend een offerte aan bij IMPERIA AI. Vertel ons kort waar u hulp bij nodig heeft en wij reageren binnen 24 uur met een passend voorstel."
        canonical="/offerte"
        keywords="offerte aanvragen, marketing offerte, seo offerte, google ads offerte, website offerte"
      />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)', padding: '120px 0 60px' }}>
        <div className="container-custom max-w-4xl mx-auto text-center px-4">
          <span style={{ color: '#D4A017', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Offerte aanvragen</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-4 mb-6" style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.1 }}>
            Vraag vrijblijvend een <span style={{ color: '#D4A017' }}>offerte</span> aan
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Vertel ons kort waar u hulp bij nodig heeft. Wij bekijken uw aanvraag globaal en nemen contact met u op voor een passend voorstel.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            {[
              'Binnen 24 uur reactie',
              'Vrijblijvende aanvraag',
              'Duidelijke vervolgstappen',
              'Geen verplichting',
            ].map(t => (
              <span key={t} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-xs md:text-sm font-semibold rounded-full px-4 py-2">
                <CheckCircle2 size={14} className="text-[#D4A017]" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="bg-white py-16">
        <div className="container-custom max-w-3xl mx-auto px-4">
          {sent ? (
            <div data-testid="offerte-success" className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>
                Bedankt voor uw aanvraag!
              </h2>
              <p className="text-[#475569] mb-6 leading-relaxed">
                Wij hebben uw aanvraag ontvangen en gaan deze direct globaal analyseren. Binnen 24 uur ontvangt u reactie van uw persoonlijke adviseur.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl">
                  Terug naar home
                </Link>
                <Link to="/werkwijze" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#D4A017] text-[#0f172a] font-semibold px-6 py-3 rounded-xl">
                  Bekijk werkwijze
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="offerte-form" className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5 shadow-lg">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">Uw naam *</label>
                  <input data-testid="offerte-naam" required value={form.naam} onChange={e => upd('naam', e.target.value)} placeholder="Jan Janssen" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">Bedrijfsnaam</label>
                  <input data-testid="offerte-bedrijf" value={form.bedrijfsnaam} onChange={e => upd('bedrijfsnaam', e.target.value)} placeholder="Janssen B.V." className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">E-mailadres *</label>
                  <input data-testid="offerte-email" required type="email" value={form.email} onChange={e => upd('email', e.target.value)} placeholder="jan@bedrijf.nl" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">Telefoonnummer</label>
                  <input data-testid="offerte-telefoon" type="tel" value={form.telefoon} onChange={e => upd('telefoon', e.target.value)} placeholder="06-12345678" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">Website</label>
                <input data-testid="offerte-website" value={form.website} onChange={e => upd('website', e.target.value)} placeholder="https://uwbedrijf.nl" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">Gewenste dienst *</label>
                  <select data-testid="offerte-dienst" required value={form.dienst} onChange={e => upd('dienst', e.target.value)} className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none">
                    <option value="">Maak een keuze…</option>
                    {DIENST_OPTIES.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">Budgetindicatie</label>
                  <select data-testid="offerte-budget" value={form.budget} onChange={e => upd('budget', e.target.value)} className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none">
                    <option value="">Maak een keuze…</option>
                    {BUDGET_OPTIES.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0f172a] mb-1.5 uppercase tracking-wide">Korte omschrijving</label>
                <textarea data-testid="offerte-omschrijving" rows={5} value={form.omschrijving} onChange={e => upd('omschrijving', e.target.value)} placeholder="Vertel kort over uw situatie en wat u wilt bereiken…" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none resize-none" />
              </div>
              <button data-testid="offerte-submit" type="submit" disabled={sending} className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold px-6 py-4 rounded-xl flex items-center justify-center gap-2 text-base shadow-lg shadow-[#D4A017]/20">
                {sending ? 'Versturen…' : <>Vraag vrijblijvend offerte aan <Send size={16} /></>}
              </button>
              <p className="text-xs text-[#94a3b8] text-center">
                * Verplichte velden. Door te versturen gaat u akkoord met onze privacyverklaring.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Post-form werkwijze */}
      <section className="bg-[#f8fafc] py-20">
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#D4A017] text-xs font-bold uppercase tracking-widest">Wat gebeurt er na uw aanvraag?</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mt-3 mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
              Van aanvraag tot start in 7 duidelijke stappen
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {POST_STEPS.map(s => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="bg-white border border-slate-200 rounded-2xl p-5 relative hover:shadow-lg hover:border-[#D4A017]/30 transition-all">
                  <div className="absolute -top-3 -left-3 w-9 h-9 bg-[#D4A017] rounded-full flex items-center justify-center font-bold text-[#0f172a] text-sm shadow-lg">
                    {s.n}
                  </div>
                  <Icon size={20} className="text-[#D4A017] mb-3" />
                  <h3 className="font-bold text-[#0f172a] text-sm mb-1.5">{s.title}</h3>
                  <p className="text-xs text-[#64748b] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-[#0f172a] py-16">
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>Liever direct contact?</h2>
          <p className="text-white/70 mb-6">Bel ons direct of stuur een bericht voor algemene vragen.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-6 py-3 rounded-xl">
              Neem contact op <ArrowRight size={16} />
            </Link>
            <Link to="/werkwijze" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl">
              Bekijk volledige werkwijze
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
